<template>
  <div class="admin-tab-entity-properties">
    <h5 v-if="typeof header === 'string' && header.length > 0">{{ header }}</h5>
    <mdb-container v-if="namedColumns" class="admin-tab-entity-properties-head">
      <mdb-row>
        <mdb-col col="4"><h6>Name</h6></mdb-col>
        <mdb-col col="4"><h6>Value</h6></mdb-col>
        <mdb-col col="4"><h6>Type</h6></mdb-col>
      </mdb-row>
    </mdb-container>
    <mdb-container class="admin-tab-entity-properties-body border">
      <mdb-row
        v-for="(property, index) in properties"
        :key="index"
      >
        <mdb-col col="4" class="pr-0">
          <mdb-input
            :read-only="isReadOnly(property)"
            size="sm"
            :value="property.key"
            class="m-0"
            :class="{ 'input-readonly': isReadOnly(property) }"
            @change="propertyKeyChange(index, $event)"
          />
        </mdb-col>
        <mdb-col col="4" class="pr-0">
          <mdb-input
            v-if="property.type === incomingPropertyDataTypes.DATETIME"
            :read-only="isReadOnly(property)"
            size="sm"
            :value="formatDateTime(property.value)"
            class="m-0"
            :class="{ 'input-readonly': isReadOnly(property) }"
            @change="propertyValueChange(index, $event)"
          />
          <mdb-select
            v-else-if="property.type === incomingPropertyDataTypes.BOOLEAN"
            :disabled="isReadOnly(property)"
            class="type-select no-margin-select"
            :label="null"
            v-model="boolPropsOptions[index]"
            @getValue="propertyValueChange(index, $event)"
          />
          <mdb-input
            v-else
            :read-only="isReadOnly(property)"
            size="sm"
            :value="property.value"
            class="m-0"
            :class="{ 'input-readonly': isReadOnly(property) }"
            @change="propertyValueChange(index, $event)"
          />
        </mdb-col>
        <mdb-col :col="isAdmin ? '2' : '4'" class="pr-0">
          <mdb-select
            :disabled="isReadOnly(property)"
            size="sm"
            class="type-select no-margin-select"
            :label="null"
            v-model="types[index]"
            @getValue="propertyTypeChange(index, $event)"
          />
        </mdb-col>
        <mdb-col v-if="isAdmin" col="2" class="pr-0">
          <mdb-btn
            gradient="sunny-morning"
            rounded
            size="sm"
            style="font-size: x-small"
            :disabled="isReadOnly(property)"
            @click.native="removeProperty(index)"
            title="Remove given property"
          >
            <mdb-icon style="font-size: x-small" icon="trash"/>
          </mdb-btn>
        </mdb-col>
      </mdb-row>
      <mdb-row>
        <mdb-col class="text-left">
          <mdb-btn
            v-if="isAdmin"
            gradient="aqua"
            rounded
            size="sm"
            class="ml-0 mr-0"
            style="font-size: x-small"
            :disabled="readOnly"
            @click.native="addProperty()"
            title="Add new property"
          >
            <mdb-icon style="font-size: x-small" icon="plus" class="mr-2"/>Add new property
          </mdb-btn>
        </mdb-col>
      </mdb-row>
    </mdb-container>
  </div>
</template>

<script>
import {mdbBtn, mdbCol, mdbContainer, mdbIcon, mdbInput, mdbRow, mdbSelect} from 'mdbvue'
import {IncomingPropertyDataTypes, InternalPropertyDataTypes, InternalPropertyDataTypesHumanReadable} from '../enums'
import {formatDT, transformDataType} from '../utils'
import {mapGetters} from 'vuex'

export default {
  name: 'AdminTabEntityProperties',
  data () {
    return {
      types: [],
      properties: [],
      boolPropsOptions: [],
      incomingPropertyDataTypes: IncomingPropertyDataTypes
    }
  },
  props: {
    header: {
      type: String,
      default: ''
    },
    namedColumns: {
      type: Boolean,
      default: true
    },
    readOnly: {
      type: Boolean,
      default: true
    },
    initialProperties: {
      type: Array,
      required: true
    }
  },
  computed: {
    ...mapGetters('user', ['isAdmin'])
  },
  methods: {
    isReadOnly (property) {
      return !this.isAdmin || this.readOnly || property.key === 'uuid' || property.type === IncomingPropertyDataTypes.DATETIME
    },
    calculateSelectsOptions () {
      this.boolPropsOptions = this.properties.map(prop => {
        return prop.type === IncomingPropertyDataTypes.BOOLEAN
          ? [
            {
              text: 'True',
              value: 1,
              selected: !!(prop.value) === true
            },
            {
              text: 'False',
              value: 0,
              selected: !!(prop.value) === false
            }
          ] : null
      })
      this.types = this.properties.map(prop => {
        return Object.values(InternalPropertyDataTypes).map(dt => {
          return {
            text: InternalPropertyDataTypesHumanReadable[dt],
            value: dt,
            selected: transformDataType(prop.type) === dt
          }
        })
      })
    },
    addProperty () {
      this.$set(
        this.properties,
        this.properties.length,
        {
          key: '<new property>',
          value: '',
          type: InternalPropertyDataTypes.STRING
        }
      )
    },
    removeProperty (index) {
      this.$delete(this.properties, index)
      this.$emit('props-change', this.properties)
    },
    propertyKeyChange (index, newKey) {
      const currentKey = this.properties[index].key
      if (currentKey === newKey) {
        return
      }
      if (typeof newKey !== 'string' || newKey === '') {
        this.$notify.warn({
          title: 'Not supported.',
          message: 'Only non-null, non-empty strings are allowed as property names.',
          position: 'top right',
          timeOut: 10000,
          closeButton: true
        })
        return
      }
      let changed = false
      const props = Array.from(
        this.properties,
        (prop, i) => {
          if (i === index) {
            changed = prop.key !== newKey
            return Object.assign(prop, {key: newKey})
          }
          return prop
        }
      )
      if (changed) {
        this.$emit('props-change', props)
      }
    },
    propertyValueChange (index, newValue) {
      const currentValue = this.properties[index].value
      if (currentValue === newValue) {
        return
      }
      if (newValue === null) {
        this.$notify.warn({
          title: 'Not supported.',
          message: 'You can not set property value to NULL.',
          position: 'top right',
          timeOut: 10000,
          closeButton: true
        })
        return
      }
      switch (transformDataType(this.properties[index].type)) {
        case InternalPropertyDataTypes.DATETIME:
          if (this.formatDateTime(currentValue) !== newValue) {
            this.$notify.warn({
              title: 'Not implemented yet.',
              message: 'Editing DATETIME type fields need to be discussed',
              position: 'top right',
              timeOut: 10000,
              closeButton: true
            })
            return
          }
          newValue = currentValue
          break
        case InternalPropertyDataTypes.NUMBER:
          if (
            typeof newValue === 'string' &&
            parseFloat(newValue.replace(/,/, '.')).toString() !== newValue.replace(/,/, '.')
          ) {
            this.$notify.warn({
              title: 'Conversion to number failed.',
              message: 'Current value can not be converted to NUMBER data type correctly',
              position: 'top right',
              timeOut: 10000,
              closeButton: true
            })
            return
          } else {
            newValue = parseFloat(newValue.replace(/,/, '.'))
          }
          break
        case InternalPropertyDataTypes.BOOLEAN:
          newValue = !!newValue
          break
        case InternalPropertyDataTypes.STRING_ARRAY:
          newValue = newValue.split(',')
          break
        case InternalPropertyDataTypes.NUMBER_ARRAY:
          newValue = newValue.split(',').map(v => parseFloat(v))
          break
      }
      let changed = false
      const props = Array.from(
        this.properties,
        (prop, i) => {
          if (i === index) {
            changed = prop.value !== newValue
            return Object.assign(prop, {value: newValue})
          }
          return prop
        }
      )
      if (changed) {
        this.$emit('props-change', props)
      }
    },
    propertyTypeChange (index, newType) {
      const currentType = this.properties[index].type
      if (currentType === newType) {
        return
      }
      const currentValue = this.properties[index].value
      let proceed = true
      let newValue = currentValue
      switch (newType) {
        case InternalPropertyDataTypes.STRING:
          newValue = currentValue.toString()
          break
        case InternalPropertyDataTypes.BOOLEAN:
          newValue = !!currentValue
          break
        case InternalPropertyDataTypes.DATETIME:
          proceed = false
          this.$notify.warn({
            title: 'Not implemented yet.',
            message: 'Conversion to DATETIME type need to be discussed',
            position: 'top right',
            timeOut: 10000,
            closeButton: true
          })
          break
        case InternalPropertyDataTypes.NUMBER:
          if ([IncomingPropertyDataTypes.INTEGER, IncomingPropertyDataTypes.FLOAT].includes(currentType)) {
            break
          }
          let tmpValue = null
          if (typeof currentValue === 'string') {
            tmpValue = parseFloat(currentValue.replace(/,/, '.'))
            if (tmpValue.toString() !== currentValue.replace(/,/, '.')) {
              proceed = false
            }
          } else if (typeof currentValue === 'boolean') {
            tmpValue = currentValue ? 1 : 0
          } else {
            proceed = false
          }
          if (proceed) {
            newValue = tmpValue
          } else {
            this.$notify.warn({
              title: 'Conversion to number failed.',
              message: 'Current value can not be converted to NUMBER data type correctly',
              position: 'top right',
              timeOut: 10000,
              closeButton: true
            })
          }
          break
        default:
          newValue = currentValue
      }
      if (proceed) {
        let changed = false
        const props = Array.from(
          this.properties,
          (prop, i) => {
            if (i === index) {
              changed = transformDataType(prop.type) !== newType
              return Object.assign(prop, {value: newValue, type: newType})
            }
            return prop
          }
        )
        if (changed) {
          this.$emit('props-change', props)
        }
      } else {
        this.calculateSelectsOptions()
      }
    },
    formatDateTime (dtObj) { return formatDT(dtObj) }
  },
  watch: {
    initialProperties: {
      handler (newValue, oldValue) {
        // To save order of properties in table if possible
        let properties = [...this.properties]
        const oldKeys = properties.map(p => p.key)
        const newKeys = this.initialProperties.map(ip => ip.key)

        if (oldKeys.some(key => !newKeys.includes(key)) || newKeys.some(key => !oldKeys.includes(key))) {
          properties = []
        }
        this.properties = [...this.initialProperties].sort((p1, p2) => {
          const p1i = properties.findIndex(p => p.key === p1.key)
          const p2i = properties.findIndex(p => p.key === p2.key)

          return p1i - p2i
        })
      },
      deep: true
    },
    properties: {
      handler () {
        this.calculateSelectsOptions()
      },
      deep: true
    }
  },
  components: {
    mdbSelect,
    mdbIcon,
    mdbBtn,
    mdbContainer,
    mdbInput,
    mdbRow,
    mdbCol
  }
}
</script>

<style lang="sass" scoped>
.admin-tab-entity-properties
  display: flex
  flex-direction: column
  height: 35%

  .admin-tab-entity-properties-head
    text-align: left

    .row
      padding-right: 1em

  .admin-tab-entity-properties-body
    display: block
    overflow-y: scroll
    height: 100%
    width: 100%

    .input-readonly
      opacity: 0.8

</style>



// WEBPACK FOOTER //
// src/components/AdminTabEntityProperties.vue